import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useUnreadChat } from '../hooks/useUnreadChat';
import { MessageCircle, Send, X, ArrowLeft, Search, Smile, Zap, Users, Wrench, ShoppingBag, ChevronDown, Plus, Volume2, VolumeX } from 'lucide-react';

const QUICK_EMOJIS = ['👋', '😊', '😂', '👍', '❤️', '🔥', '💯', '🎉', '😎', '🤝', '📱', '🔧'];

// MSN-style notification sound
const playNotifSound = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const notes = [800, 1000, 900, 1100];
    notes.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = freq;
      osc.type = 'sine';
      gain.gain.setValueAtTime(0.15, ctx.currentTime + i * 0.12);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + i * 0.12 + 0.1);
      osc.start(ctx.currentTime + i * 0.12);
      osc.stop(ctx.currentTime + i * 0.12 + 0.1);
    });
  } catch {}
};

// Nudge vibration sound effect
const playNudgeSound = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    [220, 330, 440, 330, 550].forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = freq;
      osc.type = 'square';
      gain.gain.setValueAtTime(0.12, ctx.currentTime + i * 0.08);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + i * 0.08 + 0.07);
      osc.start(ctx.currentTime + i * 0.08);
      osc.stop(ctx.currentTime + i * 0.08 + 0.07);
    });
  } catch {}
};

const ChatSidebar = () => {
  const { api, user } = useAuth();
  const { counts: unreadCounts } = useUnreadChat(api, !!user);
  const [open, setOpen] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [selectedConvo, setSelectedConvo] = useState(null);
  const [messages, setMessages] = useState([]);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [search, setSearch] = useState('');
  const [showEmojis, setShowEmojis] = useState(false);
  const [staffList, setStaffList] = useState([]);
  const [showNewDM, setShowNewDM] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [clientSearch, setClientSearch] = useState('');
  const [clientResults, setClientResults] = useState([]);
  const [dmTab, setDmTab] = useState('clients'); // 'staff' | 'clients'
  const endRef = useRef(null);
  const pollRef = useRef(null);
  const inputRef = useRef(null);
  const prevMsgCountRef = useRef(0);

  // Send heartbeat every 60s to indicate staff is online
  useEffect(() => {
    if (!api) return;
    const sendHeartbeat = () => api.post('/chat/staff/heartbeat').catch(() => {});
    sendHeartbeat();
    const hb = setInterval(sendHeartbeat, 60000);
    return () => clearInterval(hb);
  }, [api]);

  const fetchConvos = useCallback(async () => {
    if (!api) return;
    try {
      const res = await api.get('/chat/conversations');
      const convos = res.data || [];
      const totalUnread = convos.reduce((s, c) => s + (c.unread_count || 0), 0);
      if (totalUnread > prevMsgCountRef.current && prevMsgCountRef.current >= 0 && soundEnabled) {
        playNotifSound();
      }
      prevMsgCountRef.current = totalUnread;
      setConversations(convos);
    } catch {}
  }, [api, soundEnabled]);

  useEffect(() => {
    if (open && api) fetchConvos();
  }, [open, api, fetchConvos]);

  // Poll conversations list
  useEffect(() => {
    if (!open || !api) return;
    const iv = setInterval(fetchConvos, 8000);
    return () => clearInterval(iv);
  }, [open, api, fetchConvos]);

  // Poll messages for selected convo
  useEffect(() => {
    if (!selectedConvo || !api) return;
    const load = async () => {
      try {
        const res = await api.get(`/chat/conversations/${selectedConvo.id}/messages`);
        setMessages(res.data || []);
        api.post(`/chat/conversations/${selectedConvo.id}/mark-read`).catch(() => {});
      } catch {}
    };
    load();
    pollRef.current = setInterval(load, 3000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [selectedConvo, api]);

  useEffect(() => {
    if (endRef.current) endRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMsg = async () => {
    if (!message.trim() || !selectedConvo || sending) return;
    const txt = message.trim();
    setMessage('');
    setSending(true);
    try {
      await api.post(`/chat/conversations/${selectedConvo.id}/messages`, { content: txt });
      const res = await api.get(`/chat/conversations/${selectedConvo.id}/messages`);
      setMessages(res.data || []);
    } catch {}
    setSending(false);
    inputRef.current?.focus();
  };

  const sendNudge = async () => {
    if (!selectedConvo) return;
    playNudgeSound();
    try {
      await api.post(`/chat/conversations/${selectedConvo.id}/messages`, { content: '🔔 *Nudge!* 🔔' });
      const res = await api.get(`/chat/conversations/${selectedConvo.id}/messages`);
      setMessages(res.data || []);
    } catch {}
  };

  const openNewDM = async () => {
    try {
      const res = await api.get('/chat/staff-list');
      setStaffList(res.data || []);
      setShowNewDM(true);
      setDmTab('clients');
      setClientSearch('');
      setClientResults([]);
    } catch {}
  };

  const searchClients = async (query) => {
    setClientSearch(query);
    if (query.length < 2) { setClientResults([]); return; }
    try {
      const res = await api.get(`/clients/autocomplete/${encodeURIComponent(query)}`);
      setClientResults(res.data || []);
    } catch {}
  };

  const startClientDM = async (clientId) => {
    try {
      const res = await api.post('/chat/client-dm', { client_id: clientId });
      setShowNewDM(false);
      setClientSearch('');
      setClientResults([]);
      await fetchConvos();
      setSelectedConvo(res.data);
    } catch {}
  };

  const startDM = async (targetId) => {
    try {
      const res = await api.post('/chat/staff-dm', { target_id: targetId });
      setShowNewDM(false);
      await fetchConvos();
      setSelectedConvo(res.data);
    } catch {}
  };

  const getConvoIcon = (c) => {
    const t = c.related_type;
    if (t === 'staff_dm') return <Users className="w-4 h-4 text-blue-400" />;
    if (t === 'reparation' || t === 'repair') return <Wrench className="w-4 h-4 text-amber-400" />;
    return <ShoppingBag className="w-4 h-4 text-emerald-400" />;
  };

  const getConvoTitle = (c) => {
    if (c.display_title) return c.display_title;
    if (c.client_nom) return c.client_nom;
    return c.related_type === 'reparation' ? `Reparation ${c.related_id}` : `Conv. ${c.id?.slice(0, 6)}`;
  };

  const filteredConvos = conversations.filter(c => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (getConvoTitle(c) || '').toLowerCase().includes(s);
  });

  const isMyMsg = (msg) => msg.sender_id === user?.id || msg.sender_name === user?.username;

  const formatTime = (iso) => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      const now = new Date();
      const isToday = d.toDateString() === now.toDateString();
      return isToday ? d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' }) + ' ' + d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    } catch { return ''; }
  };

  if (!user) return null;

  return (
    <>
      {/* Floating Chat Button */}
      <button
        onClick={() => setOpen(!open)}
        className="fixed z-[1002] flex items-center justify-center w-14 h-14 rounded-full shadow-2xl transition-all hover:scale-110"
        style={{
          bottom: '80px', left: '20px',
          background: open ? '#ef4444' : 'linear-gradient(135deg, #3b82f6, #6366f1)',
          boxShadow: open ? '0 4px 20px rgba(239,68,68,0.4)' : '0 4px 20px rgba(99,102,241,0.4)',
        }}
        data-testid="chat-sidebar-toggle"
      >
        {open ? <X className="w-6 h-6 text-white" /> : <MessageCircle className="w-6 h-6 text-white" />}
        {!open && unreadCounts?.total > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[20px] h-5 rounded-full bg-red-500 text-white text-xs font-bold flex items-center justify-center px-1 animate-pulse">
            {unreadCounts.total > 99 ? '99+' : unreadCounts.total}
          </span>
        )}
      </button>

      {/* Chat Sidebar Panel */}
      <div
        className={`fixed top-16 left-0 bottom-0 z-[1001] transition-transform duration-300 ${open ? 'translate-x-0' : '-translate-x-full'}`}
        style={{ width: '360px', maxWidth: '100vw' }}
      >
        <div className="h-full flex flex-col bg-gray-950 border-r border-white/10 shadow-2xl overflow-hidden" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>

          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10" style={{ background: 'linear-gradient(135deg, #1e1b4b, #0f172a)' }}>
            {selectedConvo ? (
              <>
                <button onClick={() => setSelectedConvo(null)} className="p-1.5 rounded-lg hover:bg-white/10 text-gray-400" data-testid="chat-back-btn">
                  <ArrowLeft className="w-4 h-4" />
                </button>
                <div className="flex-1 ml-2 truncate">
                  <span className="text-sm font-semibold text-white">{getConvoTitle(selectedConvo)}</span>
                </div>
                <button onClick={() => setSoundEnabled(!soundEnabled)} className="p-1.5 rounded-lg hover:bg-white/10 text-gray-500">
                  {soundEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
                </button>
              </>
            ) : (
              <>
                <div className="flex items-center gap-2">
                  <MessageCircle className="w-5 h-5 text-blue-400" />
                  <span className="font-bold text-white text-sm">Messagerie</span>
                </div>
                <button onClick={openNewDM} className="p-1.5 rounded-lg hover:bg-white/10 text-blue-400" data-testid="chat-new-dm-btn">
                  <Plus className="w-4 h-4" />
                </button>
              </>
            )}
          </div>

          {/* Content */}
          {!selectedConvo ? (
            /* Conversation List */
            <div className="flex-1 overflow-y-auto">
              {/* Search */}
              <div className="px-3 py-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-500" />
                  <input
                    type="text"
                    placeholder="Rechercher..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 bg-white/5 border border-white/10 rounded-xl text-xs text-white placeholder-gray-500 focus:outline-none focus:border-blue-500/50"
                    data-testid="chat-search"
                  />
                </div>
              </div>

              {/* New DM Panel */}
              {showNewDM && (
                <div className="px-3 pb-2">
                  <div className="bg-blue-950/50 rounded-xl border border-blue-500/20 p-2">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-semibold text-blue-400">Nouveau message</span>
                      <button onClick={() => setShowNewDM(false)} className="text-gray-500 hover:text-white"><X className="w-3.5 h-3.5" /></button>
                    </div>

                    {/* Tabs: Clients / Staff */}
                    <div className="flex gap-1 mb-2 bg-white/5 rounded-lg p-0.5">
                      <button
                        onClick={() => setDmTab('clients')}
                        className={`flex-1 py-1.5 rounded-md text-[10px] font-semibold transition-all ${dmTab === 'clients' ? 'bg-cyan-600 text-white' : 'text-gray-400 hover:text-white'}`}
                        data-testid="dm-tab-clients"
                      >
                        Clients
                      </button>
                      <button
                        onClick={() => setDmTab('staff')}
                        className={`flex-1 py-1.5 rounded-md text-[10px] font-semibold transition-all ${dmTab === 'staff' ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-white'}`}
                        data-testid="dm-tab-staff"
                      >
                        Staff
                      </button>
                    </div>

                    {/* Client Search */}
                    {dmTab === 'clients' && (
                      <>
                        <input
                          type="text"
                          placeholder="Rechercher un client (nom, tel)..."
                          value={clientSearch}
                          onChange={e => searchClients(e.target.value)}
                          className="w-full px-3 py-1.5 bg-white/5 border border-white/10 rounded-lg text-xs text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500/50 mb-1.5"
                          autoFocus
                          data-testid="dm-client-search"
                        />
                        <div className="max-h-40 overflow-y-auto space-y-1">
                          {clientSearch.length < 2 ? (
                            <div className="text-[10px] text-gray-500 text-center py-2">Tapez au moins 2 caractères...</div>
                          ) : clientResults.length === 0 ? (
                            <div className="text-[10px] text-gray-500 text-center py-2">Aucun client trouvé</div>
                          ) : (
                            clientResults.map(c => (
                              <button key={c.id} onClick={() => startClientDM(c.id)} className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-white/5 text-left" data-testid={`dm-client-${c.id}`}>
                                <div className="w-7 h-7 rounded-full bg-cyan-500/20 flex items-center justify-center text-xs font-bold text-cyan-400">
                                  {(c.nom || '?')[0].toUpperCase()}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="text-xs text-white truncate">{c.nom}</div>
                                  <div className="text-[10px] text-gray-500">{c.telephone || c.email || ''}</div>
                                </div>
                              </button>
                            ))
                          )}
                        </div>
                      </>
                    )}

                    {/* Staff List */}
                    {dmTab === 'staff' && (
                      <div className="max-h-40 overflow-y-auto space-y-1">
                        {staffList.map(s => (
                          <button key={s.id} onClick={() => startDM(s.id)} className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-white/5 text-left" data-testid={`dm-staff-${s.id}`}>
                            <div className="w-7 h-7 rounded-full bg-blue-500/20 flex items-center justify-center text-xs font-bold text-blue-400">
                              {(s.username || s.nom || '?')[0].toUpperCase()}
                            </div>
                            <div>
                              <div className="text-xs text-white">{s.username || s.nom}</div>
                              <div className="text-[10px] text-gray-500">{s.role}</div>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Conversation items */}
              {filteredConvos.length === 0 ? (
                <div className="text-center py-8 text-gray-600 text-xs">Aucune conversation</div>
              ) : filteredConvos.map(c => (
                <button
                  key={c.id}
                  onClick={() => setSelectedConvo(c)}
                  className="w-full flex items-center gap-3 px-4 py-3 hover:bg-white/5 transition-colors border-b border-white/5 text-left"
                  data-testid={`chat-convo-${c.id}`}
                >
                  <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: c.related_type === 'staff_dm' ? '#1e40af20' : '#065f4620', border: '1px solid rgba(255,255,255,0.1)' }}>
                    {getConvoIcon(c)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-white truncate">{getConvoTitle(c)}</span>
                      <span className="text-[10px] text-gray-600 ml-2 shrink-0">{formatTime(c.last_message?.created_at || c.updated_at)}</span>
                    </div>
                    <p className="text-xs text-gray-500 truncate mt-0.5">{typeof c.last_message === 'string' ? c.last_message : c.last_message?.content || 'Aucun message'}</p>
                  </div>
                  {(c.unread_count || 0) > 0 && (
                    <span className="ml-1 min-w-[20px] h-5 rounded-full bg-blue-500 text-white text-[10px] font-bold flex items-center justify-center px-1">
                      {c.unread_count}
                    </span>
                  )}
                </button>
              ))}
            </div>
          ) : (
            /* Messages View */
            <>
              <div className="flex-1 overflow-y-auto px-3 py-2 space-y-2 min-h-0" data-testid="chat-messages">
                {messages.length === 0 && (
                  <div className="text-center py-8 text-gray-600 text-xs">Demarrez la conversation...</div>
                )}
                {messages.map((msg, i) => {
                  const mine = isMyMsg(msg);
                  const isNudge = msg.content?.includes('*Nudge!*');
                  return (
                    <div key={msg.id || i} className={`flex ${mine ? 'justify-end' : 'justify-start'} ${isNudge ? 'animate-bounce' : ''}`}>
                      <div
                        className={`max-w-[80%] px-3 py-2 rounded-2xl text-xs ${
                          isNudge
                            ? 'bg-amber-500/20 text-amber-300 border border-amber-500/30'
                            : mine
                            ? 'bg-blue-600 text-white rounded-br-md'
                            : 'bg-white/8 text-gray-200 border border-white/10 rounded-bl-md'
                        }`}
                      >
                        {!mine && <div className="text-[10px] font-semibold text-blue-400 mb-0.5">{msg.sender_name || 'Inconnu'}</div>}
                        <div className="break-words whitespace-pre-wrap">{msg.content}</div>
                        <div className={`text-[9px] mt-1 ${mine ? 'text-blue-200/60' : 'text-gray-600'}`}>{formatTime(msg.created_at)}</div>
                      </div>
                    </div>
                  );
                })}
                <div ref={endRef} />
              </div>

              {/* Emoji bar */}
              {showEmojis && (
                <div className="px-3 py-1.5 border-t border-white/5 flex gap-1 flex-wrap bg-gray-900/80">
                  {QUICK_EMOJIS.map(e => (
                    <button key={e} onClick={() => { setMessage(prev => prev + e); setShowEmojis(false); inputRef.current?.focus(); }} className="p-1 hover:bg-white/10 rounded text-base">{e}</button>
                  ))}
                </div>
              )}

              {/* Input */}
              <div className="shrink-0 px-3 py-2 border-t border-white/10 flex items-center gap-2" style={{ background: '#0a0a1a' }}>
                <button onClick={() => setShowEmojis(!showEmojis)} className="p-1.5 rounded-lg hover:bg-white/10 text-gray-500" data-testid="chat-emoji-btn">
                  <Smile className="w-4 h-4" />
                </button>
                <button onClick={sendNudge} className="p-1.5 rounded-lg hover:bg-white/10 text-amber-500" data-testid="chat-nudge-btn" title="Nudge">
                  <Zap className="w-4 h-4" />
                </button>
                <input
                  ref={inputRef}
                  type="text"
                  value={message}
                  onChange={e => setMessage(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && sendMsg()}
                  placeholder="Votre message..."
                  className="flex-1 bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-xs text-white placeholder-gray-600 focus:outline-none focus:border-blue-500/50"
                  data-testid="chat-input"
                />
                <button onClick={sendMsg} disabled={sending || !message.trim()} className="p-2 rounded-xl bg-blue-600 hover:bg-blue-500 text-white disabled:opacity-40 transition-colors" data-testid="chat-send-btn">
                  <Send className="w-4 h-4" />
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Overlay on mobile when open */}
      {open && <div className="fixed inset-0 bg-black/40 z-[1000] top-16 md:hidden" onClick={() => setOpen(false)} />}
    </>
  );
};

export default ChatSidebar;
